/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link, Typography, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { SectionName, UpsellType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { numberOfProducts } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus/utils/subscriptionsForwardDiscoveryExperiment';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import UpsellCard from 'bundles/unified-description-page-common/components/modules/upsell/UpsellCard';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useMWebStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import courseraPlusLogoInvert from 'images/unified-common/coursera-plus-logo-invert.svg';
import subscriptionFirstDesktopImage from 'images/unified-common/cplus-upsell-med-card-mdup.png';
import subscriptionFirstMobileImage from 'images/unified-common/cplus-upsell-small-banner-xs.png';
import courseraPlusLogo from 'images/unified-description-page/courseraplus-blue.png';
import standaloneUpsellModuleCourseraPlus from 'images/unified-description-page/individual-upsell-coursera-plus.png';
import mWebXDPCourseraPlusUsell from 'images/unified-description-page/mweb-xdp-mobile-coursera-plus-upsell.png';
import upsellModuleCourseraPlus from 'images/unified-description-page/upsell-module-coursera-plus.png';

import _t from 'i18n!nls/xdp';

type Props = {
  // This will be added dynamically from UpsellModule.
  amountOfCards?: number;
};

const styles = {
  cta: css`
    color: var(--cds-color-interactive-primary);
  `,
  subscriptionFirstInvertColor: css`
    color: var(--cds-color-neutral-primary-invert);
  `,
  wrapperContainerStyle: css`
    background-color: var(--cds-color-callouts-background-primary);

    img {
      object-position: bottom right !important;
      object-fit: contain !important;
    }
  `,
};

const CourseraPlusUpsellCard = ({ amountOfCards }: Props) => {
  const isMWebStreamlinedXDP = useMWebStreamlinedXDP();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isSubscriptionFirst = getShouldShowCourseraPlusMerchandisingPlacements();
  const isDesktop = useMediaQuery(breakpoints.up('lg'));
  const isStandaloneCard = isDesktop && amountOfCards === 1;

  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const trackingData = {
    upsellType: 'coursera_plus' as UpsellType,
    pageSection: {
      sectionName: 'upsell' as SectionName,
    },
    ...eventingProductData,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_upsell_card',
    trackingData
  );

  let backgroundImageSrc = upsellModuleCourseraPlus;
  if (isMobile && isSubscriptionFirst) {
    backgroundImageSrc = subscriptionFirstMobileImage;
  } else if (isSubscriptionFirst) {
    backgroundImageSrc = subscriptionFirstDesktopImage;
  } else if (isStandaloneCard) {
    backgroundImageSrc = standaloneUpsellModuleCourseraPlus;
  } else if (isMWebStreamlinedXDP) {
    backgroundImageSrc = mWebXDPCourseraPlusUsell;
  }

  const getBackgroundColor = () => {
    if (isSubscriptionFirst) {
      return 'var(--cds-color-callouts-background-primary)';
    } else if (isMWebStreamlinedXDP) {
      return '#eaf3ff';
    }
    return undefined;
  };

  return (
    <UpsellCard
      {...(isSubscriptionFirst && { wrapperContainerStyle: styles.wrapperContainerStyle })}
      logo={isSubscriptionFirst ? courseraPlusLogoInvert : courseraPlusLogo}
      title={
        <Typography variant="h1semibold" component="h2" color={isSubscriptionFirst ? 'invertBody' : 'body'}>
          {isSubscriptionFirst
            ? _t('Unlock access to #{numberOfProducts} courses with a subscription', { numberOfProducts })
            : _t('Open new doors with Coursera Plus')}
        </Typography>
      }
      description={
        <Typography2 variant="bodySecondary" component="p" color={isSubscriptionFirst ? 'invertBody' : 'body'}>
          {isSubscriptionFirst
            ? _t(
                'Explore roles and skills, learn more effectively with Coursera Coach, and earn recognized credentials'
              )
            : _t(
                'Unlimited access to #{numberOfProducts} world-class courses, hands-on projects, and job-ready certificate programs - all included in your subscription',
                { numberOfProducts }
              )}
        </Typography2>
      }
      cta={
        <Link
          component={TrackedA}
          href="/courseraplus"
          trackingName="upsell_cta_courseraplus"
          typographyVariant="subtitleMedium"
          variant="quiet"
          css={isSubscriptionFirst ? styles.subscriptionFirstInvertColor : styles.cta}
          {...(isSubscriptionFirst && { invert: true })}
          aria-label={_t('Learn more about Coursera Plus')}
          icon={<ArrowNextIcon />}
          onClick={() => {
            track('click_upsell_card', trackingData);
          }}
        >
          {_t('Learn more')}
        </Link>
      }
      backgroundImage={backgroundImageSrc}
      backgroundPosition="90%"
      backgroundColor={getBackgroundColor()}
      amountOfCards={amountOfCards}
      ref={visibilityTrackingRef}
    />
  );
};

export default CourseraPlusUpsellCard;
